import { useEffect, useState } from 'react';
import {
  Box,
  CloseButton,
  Divider,
  Image,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { TbInfoCircle } from 'react-icons/tb';

export interface LevelCategory {
  label: string;
  info: string;
  currentPoints: number;
  maxPoints: number;
  max: number;
}

export interface LevelsWidgetProps {
  workerName: string;
  level: number;
  experiencePoints: number;
  maxPoints: number;
  percentage: number;
  bisonImageUrl?: string;
  categories?: LevelCategory[];
}

// TODO: Replace hardcoded colors (#999, #555, #666, #e3e3e3, #979797, #eeeeee)
// with design tokens from colors.scss on full React dashboard migration (Subtask 9).

export function LevelsWidget({
  workerName,
  level,
  experiencePoints,
  maxPoints,
  percentage,
  bisonImageUrl,
  categories = [],
}: LevelsWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [categoriesAnimated, setCategoriesAnimated] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const displayPoints = Math.floor(experiencePoints);
  const displayMax = Math.floor(maxPoints);
  const clampedPercentage = Math.min(100, Math.max(0, percentage));

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    setCategoriesAnimated(false);
    const frame = requestAnimationFrame(() => setCategoriesAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [expanded]);

  // Close tooltip when clicking outside category rows
  useEffect(() => {
    if (!openTooltip) return undefined;
    const handleClick = () => setOpenTooltip(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openTooltip]);

  return (
    <Paper shadow="var(--widget-shadow)" p="md" mih={200}>
      <Stack gap={0}>
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Text fw={700} fz="lg">
            {expanded
              ? "Let's get that bison running!"
              : `${workerName}: Level ${level}`}
          </Text>
          {categories.length > 0 &&
            (expanded ? (
              <CloseButton
                onClick={() => {
                  setExpanded(false);
                  setOpenTooltip(null);
                }}
              />
            ) : (
              <UnstyledButton
                onClick={() => setExpanded(true)}
                fz={13}
                c="#666"
                style={{ whiteSpace: 'nowrap' }}
              >
                Level up meter &gt; <TbInfoCircle size={14} />
              </UnstyledButton>
            ))}
        </Group>

        <Divider mt="sm" />
        {/* Points row */}
        <Group justify="space-between" py={5} mih={30} pr={15}>
          <Text c="#999">Event Experience Points</Text>
          <Text fw={700}>
            {displayPoints}
            <Text span fz="xs" c="#999" fw={400}>
              /{displayMax}
            </Text>
          </Text>
        </Group>

        {/* Progress bar */}
        <Box mb={10}>
          {bisonImageUrl && (
            <Box pos="relative" h={30} mb={-4}>
              <Image
                src={bisonImageUrl}
                alt="Level bison"
                w={30}
                h={30}
                pos="absolute"
                style={{
                  left: animated ? `calc(${clampedPercentage}% - 15px)` : '0px',
                  transition: 'left 1.2s ease-out',
                }}
              />
            </Box>
          )}
          <Progress
            value={animated ? clampedPercentage : 0}
            h={14}
            radius={10}
            styles={{
              root: {
                backgroundColor: '#eeeeee',
                border: '1px solid #979797',
                minWidth: 200,
              },
              section: {
                background: 'linear-gradient(90deg, #00feff, #b52fff)',
                height: 12,
                borderRadius: 10,
                transition: 'width 1.2s ease-out',
              },
            }}
          />
          <Text
            fz="xs"
            fw={700}
            mt={2}
            w={32}
            style={{
              marginLeft:
                clampedPercentage > 0
                  ? `min(calc(${clampedPercentage}% - 16px), calc(100% - 32px))`
                  : 0,
            }}
          >
            {Math.round(clampedPercentage)}%
          </Text>
        </Box>

        {/* Categories section */}
        {expanded && categories.length > 0 && (
          <Stack gap={0}>
            <Text fz={13} fs="italic" c="#555" lh={1.4} mb={15}>
              Trusted Herd wants you opening doors easier, each category below
              unlocks your next status level faster! Note, while not listed,
              star scores received from companies increase and decrease total
              points.
            </Text>
            {categories.map((cat, index) => {
              const catPercentage =
                cat.currentPoints > 0 && cat.max > 0
                  ? Math.min(
                      100,
                      Math.floor((cat.currentPoints / cat.max) * 100)
                    )
                  : 0;
              const exceededMax = cat.currentPoints > cat.maxPoints;
              return (
                <Box
                  key={cat.label}
                  mb={index === categories.length - 1 ? 0 : 10}
                >
                  {/* Click-to-toggle tooltip (matches original GSP click-only behavior) */}
                  <Tooltip
                    label={cat.info}
                    multiline
                    w={220}
                    withArrow
                    opened={openTooltip === cat.label}
                  >
                    <Group
                      justify="space-between"
                      gap={0}
                      mb={5}
                      wrap="nowrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTooltip(
                          openTooltip === cat.label ? null : cat.label
                        );
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Text fz="sm" c="#999" flex={1} miw={0}>
                        {cat.label} <TbInfoCircle size={14} />
                      </Text>
                      <Text fz="sm" fw={700} style={{ whiteSpace: 'nowrap' }}>
                        {cat.currentPoints}
                        <Text span fw={400} fz="xs" c="#999">
                          /{exceededMax ? 'max' : cat.maxPoints}
                        </Text>
                      </Text>
                    </Group>
                  </Tooltip>
                  <Progress
                    value={categoriesAnimated ? catPercentage : 0}
                    h={12}
                    radius={10}
                    color="limegreen"
                    styles={{
                      root: {
                        backgroundColor: '#eeeeee',
                        border: '1px solid #979797',
                        minWidth: 200,
                      },
                      section: {
                        height: 10,
                        borderRadius: 10,
                        transition: 'width 1s ease-out',
                      },
                    }}
                  />
                  <Text
                    fz="xs"
                    fw={700}
                    mt={2}
                    w={32}
                    style={{
                      marginLeft:
                        catPercentage > 0
                          ? `min(calc(${catPercentage}% - 16px), calc(100% - 32px))`
                          : 0,
                    }}
                  >
                    {catPercentage}%
                  </Text>
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Motivational text */}
        {!expanded && (
          <Text fz="sm" fs="italic" c="#555" lh={1.4} py={5}>
            Companies want to see your event industry dedication, we make that
            easier to showcase. Let&apos;s go!
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
